import { useState } from "react";
import {
  EDIT_REASON_LABELS,
  type Job,
  type EditReason,
  type LoadDecision,
} from "@eagledispatch/shared";
import { apiFetch, ApiError } from "../../lib/api";
import Button from "../Button";
import Card from "../Card";
import FieldError from "../FieldError";

/**
 * LoadDecisionEditor — the office load-routing decision viewer/editor
 * (SPEC-54 extraction 3). Consumers: OfficeActiveJobDetail + FinishedJobDetail
 * ONLY. ReviewedJobDetail keeps its inline copy byte-untouched (bespoke
 * audit-refetch after success, generic Error handling, no toast).
 *
 * View mode: decision badge + note (or "No load decision set."). Edit mode:
 * Decision select, Note textarea, Reason-for-Edit select (REQUIRED), Save /
 * Cancel. PATCH body is the real 4-key contract (v2 R4):
 * `{ reason, note, loadDecision, loadNote }` — `reason` REQUIRED (the
 * Reason-for-Edit select), `note` = legacy edit-note (trimmed or undefined),
 * `loadDecision` + `loadNote` = the load fields.
 *
 * Error handling: `ApiError` → `fieldErrors` (both consumers do this today).
 * Toast fires only when `onToast` is provided (Active passes it; Finished
 * does not).
 *
 * The CARD and viewer always render unconditionally — an active job's page
 * still shows the Load Decision card (with the "No load decision set." empty
 * state); only the Edit/Set button is gated by `editable`. SPEC-55 (review-
 * first flow): the product contract is that load decisions are set on
 * REVIEWED jobs — both consumers now pass `editable={job.state ===
 * "REVIEWED"}`, and when the button is gated off AND no decision exists, a
 * muted hint tells the office to review first. When a decision exists it
 * displays as before, no hint. `editable` defaults to true. Renders a bare
 * `<Card>`; grid wrappers stay inline in the consumers.
 */
export default function LoadDecisionEditor({
  job,
  onUpdated,
  onToast,
  editable = true,
}: {
  job: Job;
  onUpdated: (job: Job) => void;
  onToast?: (msg: string, kind?: "success" | "error") => void;
  editable?: boolean;
}) {
  const [loadDecisionEditing, setLoadDecisionEditing] = useState(false);
  const [loadDecision, setLoadDecision] = useState<LoadDecision | "">("");
  const [loadNote, setLoadNote] = useState("");
  const [loadDecisionReason, setLoadDecisionReason] = useState<EditReason>("OTHER");
  const [loadDecisionSubmitting, setLoadDecisionSubmitting] = useState(false);
  const [loadDecisionSubmitError, setLoadDecisionSubmitError] = useState<string | null>(null);
  const [loadDecisionFieldErrors, setLoadDecisionFieldErrors] = useState<Record<string, string>>({});

  const handleLoadDecisionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!job) return;
    setLoadDecisionSubmitError(null);
    setLoadDecisionFieldErrors({});

    const payload: Record<string, unknown> = {
      reason: loadDecisionReason,
      note: loadNote.trim() || undefined,
    };
    if (loadDecision) payload.loadDecision = loadDecision;
    if (loadNote.trim()) payload.loadNote = loadNote.trim();

    setLoadDecisionSubmitting(true);
    try {
      const updated = await apiFetch<Job>(`/api/v1/jobs/${job.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      onUpdated(updated);
      setLoadDecisionEditing(false);
      if (onToast) onToast(`Load decision updated for ${updated.jobNumber}.`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setLoadDecisionFieldErrors(err.fieldErrors);
        } else {
          setLoadDecisionSubmitError(err.message);
        }
      } else {
        setLoadDecisionSubmitError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoadDecisionSubmitting(false);
    }
  };

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h2>Load Decision</h2>
        {editable && !loadDecisionEditing && (
          <Button variant="secondary" onClick={() => {
            setLoadDecisionEditing(true);
            setLoadDecision(job.loadDecision ?? "");
            setLoadNote(job.loadNote ?? "");
          }}>
            {job.loadDecision ? "Edit" : "Set Decision"}
          </Button>
        )}
      </div>

      {loadDecisionSubmitError && (
        <p className="form-error err" style={{ marginBottom: "12px" }}>{loadDecisionSubmitError}</p>
      )}

      {!loadDecisionEditing ? (
        <div>
          {job.loadDecision ? (
            <>
              <div
                className={job.loadDecision === "LANDFILL" ? "tag tag-blue" : "tag tag-green"}
                style={{ marginBottom: "8px", display: "inline-block" }}
              >
                {job.loadDecision === "LANDFILL" ? "Load Decision: LANDFILL" : "Load Decision: FACILITY METAL"}
              </div>
              {job.loadNote && <p className="muted">{job.loadNote}</p>}
            </>
          ) : (
            <>
              <p className="muted">No load decision set.</p>
              {!editable && (
                <p className="muted" style={{ fontSize: "13px" }}>
                  Review this job first to set the load decision.
                </p>
              )}
            </>
          )}
        </div>
      ) : (
        <form onSubmit={handleLoadDecisionSubmit} className="form-stack">
          <div className="form-group">
            <label>Decision</label>
            <select
              value={loadDecision}
              onChange={(e) => setLoadDecision(e.target.value as LoadDecision | "")}
            >
              <option value="">— Select —</option>
              <option value="LANDFILL">Landfill</option>
              <option value="FACILITY_METAL">Facility (Pull Metal)</option>
            </select>
          </div>
          <div className="form-group">
            <label>Note (optional)</label>
            <textarea
              rows={2}
              value={loadNote}
              onChange={(e) => setLoadNote(e.target.value)}
              placeholder="e.g. mixed metal visible, route to facility"
              maxLength={2000}
            />
          </div>
          <div className="form-group">
            <label>Reason for Edit*</label>
            <select
              value={loadDecisionReason}
              onChange={(e) => setLoadDecisionReason(e.target.value as EditReason)}
              required
            >
              {Object.entries(EDIT_REASON_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <FieldError message={loadDecisionFieldErrors.reason} />
          </div>
          <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
            <Button type="submit" disabled={loadDecisionSubmitting}>
              {loadDecisionSubmitting ? "Saving…" : "Save Decision"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setLoadDecisionEditing(false);
                setLoadDecisionSubmitError(null);
                setLoadDecisionFieldErrors({});
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}